<template>
    <div id="home">
        <div id="body" v-if="hasCheck" class="content-spacer">
            <CheckDetail :detail="checkDetail" />

            <!-- Wrap the rest in a div so we can apply a background color -->
            <div id="rest-of-body" class="drop-shadow-sides">
                <PaymentOptions />
            </div>
        </div>
    </div>
</template>

<script>
    import CheckDetail from "../components/CheckDetail";
    import PaymentOptions from "../components/PaymentOptions";
    import axios from "axios";
    import braintree from "braintree-web";
    import { sweetAlert2 } from "../mixins/sweet-alert2";
    import { showBlocker } from "../mixins/show-blocker";
    import { mapState, mapMutations } from "vuex";
    import { paymentProcessors } from "../constants/payment-processors";
    import { getPaymentMethod } from "../constants/payment-methods";
    import { keynames } from "../constants/keynames";
    import sessionDataStorage from "../mixins/session-storage";
    import { appSettings } from "../mixins/app-settings";

    export default {
        name: "NsPay",
        mixins: [sweetAlert2, showBlocker, sessionDataStorage, appSettings],
        components: {
            CheckDetail,
            PaymentOptions
        },
        data: function () {
            return {
                checkReady: false
            }
        },
        computed: {
            ...mapState({
                session: state => state.session,
                spriteImagesReady: state => state.session.spriteImagesReady,
                creditCardInitCompleted: state => state.session.creditCardInitCompleted,
                apiBaseUrl: state => state.session.apiBaseUrl
            }),
            checkDetail() {
                return this.session.check;
            },
            hasCheck() {
                return this.session.check !== null;
            }
        },
        watch: {
            spriteImagesReady: "revealPageWhenAllReady",
            checkReady: "revealPageWhenAllReady"
        },
        methods: {
            ...mapMutations([
                "set",
                "setLoadingStatus",
                "setCreditCardPaymentClient",
                "setProcessor",
                "updatePayment",
                "setApplePayAndGooglePaySettings"
            ]),
            revealPageWhenAllReady() {
                if (this.spriteImagesReady && this.checkReady) {
                    this.setLoadingStatus(false);
                }
            },
            async initializeBraintreeCreditCardClient() {
                const siteId = this.session.siteId;
                const accountId = this.session.accountId;
                const url = this.getApiBaseUrl() + "/payment/clientToken/" + siteId + "/" + accountId;
                let hasExited = false;

                let clientToken = await axios.get(url)
                    .then(response => {
                        if (response.data) {
                            return response.data
                        }
                        else {
                            this.setCreditCardPaymentClient({
                                creditCardInitCompleted: true,
                                creditCardPaymentClient: null
                            });
                        }
                    })
                    .catch(error => {
                        if (error.response &&
                            error.response.data &&
                            error.response.data.errorCode) {
                            this.$router.push({ name: "Error", params: { errorCode: error.response.data.errorCode } });
                            hasExited = true;
                        }
                        else {
                            // Only log the errors for now. User may not visit the
                            // credit card page, so let the credit card page handle
                            // the error.
                            console.error(error);
                            this.setCreditCardPaymentClient({
                                creditCardInitCompleted: true,
                                creditCardPaymentClient: null
                            });
                        }

                    })

                if (hasExited) {
                    return;
                }

                if (clientToken) {
                    braintree.client.create(
                        {
                            authorization: clientToken
                        },
                        (err, clientInstance) => {
                            if (err) {
                                // Same as above regarding error handling.
                                console.error(err);
                                this.setCreditCardPaymentClient({
                                    creditCardInitCompleted: true,
                                    creditCardPaymentClient: null
                                });
                                return;
                            }

                            this.setCreditCardPaymentClient({
                                creditCardInitCompleted: true,
                                creditCardPaymentClient: clientInstance
                            });
                        });
                }
                else {
                    this.setCreditCardPaymentClient({
                        creditCardInitCompleted: true,
                        creditCardPaymentClient: null
                    });
                    return;
                }
            }
        },
        async mounted() {
            // Clear stored url.
            this.removeSessionData(keynames.checkUrl);

            if (this.session.check !== null) {
                return;
            }

            const siteId = this.session.siteId;
            const accountId = this.session.accountId;
            const checkId = this.session.checkId;
            const accessToken = this.session.accessToken;

            if (siteId === undefined || accountId === undefined || checkId === undefined || accessToken === undefined) {
                // We could be here if user triggers a page refresh. To reload the parameters, simply
                // go back to the previous pagae.
                // TODO: Only go back to the previous page if the previous page is "landing".
                this.$router.go(-1);
                return;
            }

            const applePayAndGooglePaySettings = this.getApiBaseUrl() + "/payment/settingsApplePayGooglePay";

            await axios.get(applePayAndGooglePaySettings)
                .then(response => {
                    if (response.data) {
                        this.setApplePayAndGooglePaySettings(response.data);
                    }
                })
                .catch(error => {
                    console.debug(error);
                    this.$router.push({ name: "Error", params: { errorCode: "1022" } });
                    return;
                })

            // Generate a UUID for this device.
            let deviceId = this.$uuid.v1();

            // Save the device id on local storage if enabled.
            // If local storage is not enabled, rescan of QR code or refreshing of browser is not allowed.
            try {
                if (localStorage.getItem("NSPayDeviceId")) {
                    deviceId = localStorage.getItem("NSPayDeviceId");
                }
                else {
                    localStorage.setItem("NSPayDeviceId", deviceId);
                }
            }
            catch (error) {
                console.log("Local storage is not enabled. " + error);
            }

            let hasExited = false;
            const checkUrl = this.getApiBaseUrl() + "/check/" + siteId + "/" + accountId + "/" + checkId + "/" + deviceId;            
            this.startProgressIndicator();

            await axios.get(checkUrl)
                .then(response => {
                    if (response.data) {
                        this.set(response.data.check);

                        if (this.hasCheck && this.checkDetail.balance === 0) {
                            // Allow action buttons if it's a redirect from payment.
                            if (this.session.paymentId !== undefined) {
                                if (this.session.payment == null) {
                                    let payment = this.checkDetail.payments?.find(p => p.paymentId == this.session.paymentId);
                                    
                                    if (payment != null) {
                                        payment.paymentName = getPaymentMethod(this.session.paymentMethod);
                                        this.updatePayment(payment);
                                        this.$router.push({ name: "PaymentSummary" });
                                        return;
                                    }
                                }
                            }

                            this.$router.push({
                                name: "PaymentSummary",
                                query: { a: "0" } // Hide action buttons of the page.
                            });
                            
                            return;
                        }
                    }
                    else {
                        this.$router.push({ name: "Error", params: { errorCode: "1000" } });
                        hasExited = true;
                    }                  
                })
                .catch(error => {
                    console.debug(error);
                    if (error.response &&
                        error.response.data &&
                        error.response.data.errorCode) {
                        this.$router.push({ name: "Error", params: { errorCode: error.response.data.errorCode } });
                    }
                    else if (error.message.includes("401")) {
                        this.$router.push({ name: "Error", params: { errorCode: "401" } });
                    }
                    else {
                        this.$router.push({ name: "Error", params: { errorCode: "1004" } });
                    }
                    hasExited = true;
                })
                .finally(() => {
                    this.checkReady = true;
                    this.stopProgressIndicator();
                })

            if (hasExited) {
                return;
            }

            const processorUrl = this.getApiBaseUrl() + "/payment/paymentProcessor/" + siteId + "/" + accountId;

            await axios.get(processorUrl)
                .then(response => {
                    if (response.data) {
                        this.setProcessor(response.data);
                    }
                })
                .catch(error => {
                    console.debug(error);
                    this.$router.push({ name: "Error", params: { errorCode: "1022" } });
                    return;
                })

            // Prepare credit card client in the background.
            if (!this.creditCardInitCompleted) {
                if (this.session.paymentProcessor === paymentProcessors.braintree)
                    await this.initializeBraintreeCreditCardClient();
            }
        }
    }
</script>

<style scoped>
    #home {
        display: flex;
    }

    #error {
        margin: 0 auto;
    }

    #body {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
    }

    #rest-of-body {
        flex-grow: 1;
        background-color: white;
        padding-bottom: 60px;
    }
</style>